import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_DiseaseAddressedFragment = gql(/* GraphQL */ `
  fragment Index_DiseaseAddressedFragment on HealthcareIndicators {
    diseaseAddressed {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type DiseaseAddressedProps = {
  className?: string;
  indicators: FragmentType<typeof Index_DiseaseAddressedFragment>;
};

export function DiseaseAddressed({ indicators, className }: DiseaseAddressedProps) {
  const { diseaseAddressed } = useFragment(Index_DiseaseAddressedFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip } =
    useChartTranslations('diseaseAddressed');

  const sortedItems = useMemo(() => {
    const colors = generateChartColors(diseaseAddressed.items.length);
    const sorted = orderBy(diseaseAddressed.items, ['pct'], ['desc']);
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [diseaseAddressed.items]);

  const min = Math.min(0, ...diseaseAddressed.items.map((item) => item?.count ?? 0));
  const max = Math.max(...diseaseAddressed.items.map((item) => item?.count ?? 0));

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip>{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={diseaseAddressed.isDataSuppressed}>
        <HorizontalBars min={min} max={max} showInlineDisplayValue={false}>
          {sortedItems.map((item, i) => {
            if (!item) return null;
            return (
              <HorizontalBars.Item
                key={i}
                label={getOptionLabel(item.label)}
                tooltipLabel={getOptionTooltip(item.label)}
                value={item.count ?? null}
                displayValue={item.count?.toLocaleString() ?? ''}
                color={item.color}
              />
            );
          })}
        </HorizontalBars>
      </ChartCard.Body>
    </ChartCard>
  );
}
